import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi, workflowTemplatesApi } from '../../api/admin';
import toast from 'react-hot-toast';
import {
  Plus, Trash2, ArrowRight, Pencil, Copy,
  ChevronRight, Settings, X, Check, Package, GitBranch
} from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';

import TaskFlowEditor from '../../components/admin/TaskFlowEditor';
import WorkflowTemplateStore from "../../components/admin/WorkflowTemplateStore";

const STAGE_COLORS = [
  '#6366F1', '#8B5CF6', '#EC4899', '#EF4444', '#F97316',
  '#EAB308', '#22C55E', '#14B8A6', '#06B6D4', '#3B82F6',
  '#6B7280', '#1E293B',
];

const TRANSITION_STYLES = [
  { value: 'primary', label: 'Primary (Blue)', className: 'bg-brand-600 text-white' },
  { value: 'success', label: 'Success (Green)', className: 'bg-green-600 text-white' },
  { value: 'warning', label: 'Warning (Yellow)', className: 'bg-yellow-500 text-white' },
  { value: 'danger', label: 'Danger (Red)', className: 'bg-red-600 text-white' },
  { value: 'secondary', label: 'Secondary (Gray)', className: 'bg-gray-500 text-white' },
];

export default function WorkflowBuilder() {
  const { recordTypeId } = useParams();
  const navigate = useNavigate();
  const [recordType, setRecordType] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [workflow, setWorkflow] = useState(null);
  const [wfTransitions, setWfTransitions] = useState([]);
  const [showWfTransForm, setShowWfTransForm] = useState(false);
  const [editingWfTrans, setEditingWfTrans] = useState(null);
  const [screens, setScreens] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStage, setEditingStage] = useState(null);
  const [editingTransition, setEditingTransition] = useState(null);
  const [showAddStage, setShowAddStage] = useState(false);
  const [showTemplateStore, setShowTemplateStore] = useState(false);
  const [showSaveToStore, setShowSaveToStore] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: '', description: '', practice_area: 'general', icon: 'briefcase', color: '#6366F1' });
  const [saveLoading, setSaveLoading] = useState(false);
  const [showAddTransition, setShowAddTransition] = useState(false);
  const transitionDirtyRef = useRef(false);
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState('#6366F1');

  const fetchData = useCallback(async () => {
    try {
      const rtRes = await adminApi.getRecordType(recordTypeId);
      setRecordType(rtRes.data.record_type);

      const wfRes = await adminApi.getWorkflows(recordTypeId);
      setWorkflows(wfRes.data.workflows);
      if (wfRes.data.workflows.length > 0) {
        setWorkflow(wfRes.data.workflows[0]);
      }
      try {
        const wftRes = await adminApi.getWorkflowTransitions(recordTypeId);
        setWfTransitions(wftRes.data.workflow_transitions);
      } catch (e) {}

      const scrRes = await adminApi.getScreens(recordTypeId);
      setScreens(scrRes.data.screens);

      const fldRes = await adminApi.getFields(recordTypeId);
      setFields(fldRes.data.fields);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [recordTypeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createWorkflow = async (name) => {
    try {
      const wfName = name || (recordType.name + ' Workflow' + (workflows.length > 0 ? ' ' + (workflows.length + 1) : ''));
      const res = await adminApi.createWorkflow({
        record_type_id: recordTypeId,
        name: wfName,
      });
      const newWf = { ...res.data.workflow, stages: [], transitions: [] };
      setWorkflows(function(prev) { return prev.concat([newWf]); });
      setWorkflow(newWf);
      toast.success('Workflow created');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create workflow');
    }
  };

  const addStage = async () => {
    if (!newStageName.trim()) return;
    try {
      const res = await adminApi.addWorkflowStage(workflow.id, {
        name: newStageName.trim(),
        color: newStageColor,
      });
      setWorkflow(prev => ({
        ...prev,
        stages: [...prev.stages, res.data.stage],
      }));
      setNewStageName('');
      setNewStageColor('#6366F1');
      setShowAddStage(false);
      toast.success('Stage added');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add stage');
    }
  };

  const updateStage = async (stageId, data) => {
    try {
      const res = await adminApi.updateWorkflowStage(workflow.id, stageId, data);
      setWorkflow(prev => ({
        ...prev,
        stages: prev.stages.map(s => s.id === stageId ? { ...s, ...res.data.stage } : s),
      }));
      toast.success('Stage updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update stage');
    }
  };

  const deleteStage = async (stageId) => {
    if (!window.confirm('Delete this stage? This cannot be undone.')) return;
    try {
      await adminApi.deleteWorkflowStage(workflow.id, stageId);
      setWorkflow(prev => ({
        ...prev,
        stages: prev.stages.filter(s => s.id !== stageId),
        transitions: prev.transitions.filter(t => t.from_stage_id !== stageId && t.to_stage_id !== stageId),
      }));
      setEditingStage(null);
      toast.success('Stage deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete stage');
    }
  };

  const setInitialStage = async (stageId) => {
    for (const stage of workflow.stages) {
      if (stage.settings?.is_initial && stage.id !== stageId) {
        await updateStage(stage.id, { settings: { ...stage.settings, is_initial: false } });
      }
    }
    const stage = workflow.stages.find(s => s.id === stageId);
    await updateStage(stageId, { settings: { ...(stage?.settings || {}), is_initial: true } });
    fetchData();
  };

  const addTransition = async (data) => {
    try {
      const res = await adminApi.addWorkflowTransition(workflow.id, data);
      setWorkflow(prev => ({
        ...prev,
        transitions: [...prev.transitions, res.data.transition],
      }));
      setShowAddTransition(false);
      toast.success('Transition added');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add transition');
    }
  };

  const updateTransition = async (transId, data) => {
    try {
      const res = await adminApi.updateWorkflowTransition(workflow.id, transId, data);
      setWorkflow(prev => ({
        ...prev,
        transitions: prev.transitions.map(t => t.id === transId ? { ...t, ...res.data.transition } : t),
      }));
      toast.success('Transition updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update transition');
    }
  };

  const deleteTransition = async (transId) => {
    try {
      await adminApi.deleteWorkflowTransition(workflow.id, transId);
      setWorkflow(prev => ({
        ...prev,
        transitions: prev.transitions.filter(t => t.id !== transId),
      }));
      setEditingTransition(null);
      toast.success('Transition deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const selectTransition = (t) => {
    if (editingTransition && editingTransition.id !== t.id && transitionDirtyRef.current) {
      if (!window.confirm('You have unsaved changes to this transition. Leave without saving?')) return;
    }
    transitionDirtyRef.current = false;
    setEditingTransition(t);
    setEditingStage(null);
    setShowAddTransition(false);
  };

  const closeTransition = () => {
    if (transitionDirtyRef.current) {
      if (!window.confirm('You have unsaved changes to this transition. Leave without saving?')) return;
    }
    transitionDirtyRef.current = false;
    setEditingTransition(null);
  };

  const deleteWorkflow = async () => {
    if (!window.confirm('Delete this entire workflow and all its stages? This cannot be undone.')) return;
    try {
      var deletedId = workflow.id;
      await adminApi.deleteWorkflow(deletedId);
      var remaining = workflows.filter(function(w) { return w.id !== deletedId; });
      setWorkflows(remaining);
      setWorkflow(remaining.length > 0 ? remaining[0] : null);
      toast.success('Workflow deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <AdminPageHeader title="Workflow Builder" description={recordType?.name} icon={GitBranch} iconColor="text-green-600" iconBg="bg-green-50" backTo="/admin/record-types" />
      <div className="flex items-center justify-end mb-6">
        {workflow && (
          <div className="flex items-center gap-2">
            <button onClick={async () => {
              await workflowTemplatesApi.setDefault(workflow.id, recordTypeId);
              const res = await adminApi.getWorkflows(recordTypeId);
              setWorkflows(res.data.workflows);
              setWorkflow(res.data.workflows.find(w => w.id === workflow.id));
              toast.success('Set as default workflow');
            }}
              disabled={workflow.is_default}
              className={"flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border " + (workflow.is_default ? "text-amber-600 border-amber-200 bg-amber-50 cursor-default" : "text-gray-600 border-gray-200 hover:bg-gray-50")}>
              {workflow.is_default ? "Default Workflow" : "Set as Default"}
            </button>
            <button onClick={function() { var newName = prompt("Rename workflow (max 20 chars):", workflow.name); if (newName && newName.trim() !== workflow.name) { if (newName.trim().length > 20) { toast.error("Workflow name must be 20 characters or less"); return; } adminApi.updateWorkflow(workflow.id, { name: newName.trim() }).then(function() { return adminApi.getWorkflows(recordTypeId); }).then(function(res) { setWorkflows(res.data.workflows); setWorkflow(res.data.workflows.find(function(w) { return w.id === workflow.id; })); toast.success("Workflow renamed"); }).catch(function(err) { toast.error("Rename failed"); }); } }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              <Pencil className="h-4 w-4" /> Rename
            </button>
            <button onClick={function() { var copyName = prompt("Name for copy (max 20 chars):", (workflow.name + " (Copy)").slice(0, 20)); if (copyName) { if (copyName.trim().length > 20) { toast.error("Workflow name must be 20 characters or less"); return; } adminApi.copyWorkflow(workflow.id, copyName.trim()).then(function(res) { return adminApi.getWorkflows(recordTypeId).then(function(wfRes) { setWorkflows(wfRes.data.workflows); setWorkflow(wfRes.data.workflows.find(function(w) { return w.id === res.data.workflow.id; })); toast.success("Workflow copied"); }); }).catch(function(err) { toast.error(err.response?.data?.error || "Copy failed"); }); } }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              <Copy className="h-4 w-4" /> Copy
            </button>
            <button onClick={function() { setSaveForm({ name: workflow.name, description: workflow.description || "", practice_area: "general", icon: "briefcase", color: "#6366F1" }); setShowSaveToStore(true); }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50">
              <Package className="h-4 w-4" /> Save to Store
            </button>
            <button onClick={deleteWorkflow}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50">
              <Trash2 className="h-4 w-4" /> Delete Workflow
            </button>
          </div>
        )}
      </div>

      {/* No workflow yet */}
      {!workflow && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="h-16 w-16 rounded-2xl bg-brand-50 flex items-center justify-center mx-auto mb-4">
            <Settings className="h-8(w-8 text-brand-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No workflow defined</h3>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            Create a workflow to define the stages records move through — like Intake → Review → Active → Closed.
          </p>
          <div className="flex items-center gap-3 justify-center"><button onClick={() => createWorkflow()}
            className="inline-flex items-center gap-2 bg-brand-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-700">
            <Plus className="h-4 w-4" /> Create Blank
          </button><button onClick={() => setShowTemplateStore(true)} className="inline-flex items-center gap-2 bg-brand-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-700"><Package className="h-4 w-4" /> Browse Templates</button></div>
        </div>
      )}

      {/* Workflow Tabs */}
      {workflows.length > 0 && (
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-1 overflow-x-auto">
            {workflows.map(function(wf) {
              return (
                <button key={wf.id} onClick={function() { if (transitionDirtyRef.current && !window.confirm('You have unsaved changes to this transition. Leave without saving?')) return; transitionDirtyRef.current = false; setWorkflow(wf); setEditingStage(null); setEditingTransition(null); }}
                  className={"px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-all " +
                    (workflow && workflow.id === wf.id
                      ? "bg-white text-brand-700 shadow-sm"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50")}>
                  {wf.name}
                  <span className="ml-1.5 text-[10px] text-gray-400">{(wf.stages || []).length} stages</span>
                  {wf.is_default && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Default</span>}
                </button>
              );
            })}
          </div>
          <button onClick={function() { var name = prompt("Workflow name (max 20 chars):", (recordType.name + " Workflow " + (workflows.length + 1)).slice(0, 20)); if (name) { if (name.trim().length > 20) { toast.error("Workflow name must be 20 characters or less"); return; } createWorkflow(name.trim()); } }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50 flex-shrink-0">
            <Plus className="h-4 w-4" /> New Workflow
          </button><button onClick={() => setShowTemplateStore(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 flex-shrink-0"><Package className="h-4 w-4" /> Browse Templates</button>
        </div>
      )}

      {/* Workflow Editor */}
      {workflow && (
        <div className="space-y-6">
          {/* Stage Pipeline */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900">Stages</h2>
              <button onClick={() => setShowAddStage(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50">
                <Plus className="h-3.5 w-3.5" /> Add Stage
              </button>
            </div>

            {workflow.stages.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No stages yet. Add your first stage to get started.
              </div>
            ) : (
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {workflow.stages.map((stage, idx) => (
                  <React.Fragment key={stage.id}>
                    <div
                      onClick={() => { if (transitionDirtyRef.current && !window.confirm('You have unsaved changes to this transition. Leave without saving?')) return; transitionDirtyRef.current = false; setEditingStage(stage); setEditingTransition(null); }}
                      className={`flex-shrink-0 relative cursor-pointer rounded-xl border-2 px-4 py-3 min-w-[140px] transition-all hover:shadow-md ${
                        editingStage?.id === stage.id ? 'border-brand-500 shadow-md' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                        <span className="text-sm font-semibold text-gray-900 truncate">{stage.name}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        {stage.settings?.is_initial && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-medium">START</span>
                        )}
                        {stage.settings?.is_final && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">END</span>
                        )}
                        {stage.screen_name && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium truncate max-w-[80px]">{stage.screen_name}</span>
                        )}
                      </div>
                    </div>
                    {idx < workflow.stages.length - 1 && (
                      <ChevronRight className="h-5 w-5 text-gray-300 flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Add Stage Inline Form */}
            {showAddStage && (
              <div className="mt-4 p-4 border border-brand-200 rounded-xl bg-brand-50/30">
                <div className="flex items-center gap-3">
                  <input type="text" value={newStageName} onChange={e => setNewStageName(e.target.value)}
                    placeholder="Stage name..."
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                    autoFocus onKeyDown={e => e.key === 'Enter' && addStage()} />
                  <div className="flex items-center gap-1">
                    {STAGE_COLORS.slice(0, 6).map(c => (
                      <button key={c} onClick={() => setNewStageColor(c)}
                        className={`h-6 w-6 rounded-full border-2 transition-all ${newStageColor === c ? 'border-gray-900 scale-110' : 'border-transparent'}`}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                  <button onClick={addStage} disabled={!newStageName.trim()}
                    className="px-3 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50">
                    <Check className="h-4 w-4" />
                  </button>
                  <button onClick={() => { setShowAddStage(false); setNewStageName(''); }}
                    className="px-3 py-2 text-gray-500 text-sm hover:text-gray-700">
                    <X className="h-4(w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Stage Detail Editor */}
          {editingStage && (
            <StageEditor
              stage={editingStage}
              workflowId={workflow.id}
              screens={screens}
              fields={fields}
              onUpdate={(data) => updateStage(editingStage.id, data)}
              onSetInitial={() => setInitialStage(editingStage.id)}
              onDelete={() => deleteStage(editingStage.id)}
              onClose={() => setEditingStage(null)}
            />
          )}

          {/* Transitions */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900">Transitions</h2>
              {workflow.stages.length >= 2 && (
                <button onClick={() => { if (transitionDirtyRef.current && !window.confirm('You have unsaved changes to this transition. Leave without saving?')) return; transitionDirtyRef.current = false; setShowAddTransition(true); setEditingTransition(null); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50">
                  <Plus className="h-.5 w-3.5" /> Add Transition
                </button>
              )}
            </div>

            {workflow.stages.length < 2 ? (
              <div className="text-center py-6 text-sm text-gray-400">
                Add at least 2 stages to define transitions.
              </div>
            ) : workflow.transitions.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-400">
                No transitions yet. Add transitions to allow records to move between stages.
              </div>
            ) : (
              <div className="space-y-2">
                {workflow.transitions.map(t => {
                  const fromStage = workflow.stages.find(s => s.id === t.from_stage_id);
                  const toStage = workflow.stages.find(s => s.id === t.to_stage_id);
                  const styleObj = TRANSITION_STYLES.find(s => s.value === t.style) || TRANSITION_STYLES[0];
                  return (
                    <div key={t.id}
                      onClick={() => selectTransition(t)}
                      className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:shadow-sm transition-all ${editingTransition?.id === t.id ? 'border-brand-500 bg-brand-50/30' : 'border-gray-200'}`}>
                      <div className="flex items-center gap-2 text-sm">
                        {fromStage ? (
                          <>
                            <span className="flex items-center gap-1.5">
                              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: fromStage.color }} />
                              <span className="font-medium text-gray-700">{fromStage.name}</span>
                            </span>
                            <ArrowRight className="h-4 w-4 text-gray-400" />
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-gray-400 italic">Any stage</span>
                            <ArrowRight className="h-4 w-4 text-gray-400" />
                          </>
                        )}
                        <span className="flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: toStage?.color }} />
                          <span className="font-medium text-gray-700">{toStage?.name}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-md font-medium ${styleObj.className}`}>
                          {t.label}
                        </span>
                        {t.require_note && (
                          <span className="text-[10px] text-gray-400">note required</span>
                        )}
                        <Pencil className="h-3.5 w-3.5 text-gray-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {showAddTransition && (
              <TransitionForm
                stages={workflow.stages}
                screens={screens}
                onSave={addTransition}
                onClose={() => setShowAddTransition(false)}
              />
            )}

            {editingTransition && (
              <TransitionForm
                key={editingTransition.id}
                stages={workflow.stages}
                screens={screens}
                initial={editingTransition}
                onSave={(data) => { transitionDirtyRef.current = false; updateTransition(editingTransition.id, data); setEditingTransition(null); }}
                onDelete={() => { transitionDirtyRef.current = false; deleteTransition(editingTransition.id); }}
                onClose={closeTransition}
                onDirtyChange={(dirty) => { transitionDirtyRef.current = dirty; }}
              />
            )}
          </div>
        </div>
      )}

      {/* Cross-Workflow Transitions */}
      {workflows.length >= 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Cross-Workflow Transitions</h2>
              <p className="text-xs text-gray-500 mt-0.5">Define how records move between workflows (e.g. Pre-Litigation → Litigation)</p>
            </div>
            <button onClick={function() { setEditingWfTrans(null); setShowWfTransForm(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50">
              <Plus className="h-3.5 w-3.5" /> Add Transition
            </button>
          </div>

          {wfTransitions.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-400">
              No cross-workflow transitions defined. Add one to let records move between workflows.
            </div>
          ) : (
            <div className="space-y-2">
              {wfTransitions.map(function(t) {
                var fromWf = workflows.find(function(w) { return w.id === t.from_workflow_id; });
                var toWf = workflows.find(function(w) { return w.id === t.to_workflow_id; });
                var fromStage = fromWf && fromWf.stages ? fromWf.stages.find(function(s) { return s.id === t.from_stage_id; }) : null;
                var toStage = toWf && toWf.stages ? toWf.stages.find(function(s) { return s.id === t.to_stage_id; }) : null;
                var styleObj = TRANSITION_STYLES.find(function(s) { return s.value === t.style; }) || TRANSITION_STYLES[0];
                return (
                  <div key={t.id}
                    onClick={function() { setEditingWfTrans(t); setShowWfTransForm(false); }}
                    className={"flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:shadow-sm transition-all " + (editingWfTrans?.id === t.id ? 'border-brand-500 bg-brand-50/30' : 'border-gray-200')}>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-gray-700">{fromWf ? fromWf.name : 'Any workflow'}</span>
                      {fromStage && <span className="text-xs text-gray-400">({fromStage.name})</span>}
                      <ArrowRight className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-700">{toWf ? toWf.name : '?'}</span>
                      {toStage ? <span className="text-xs text-gray-400">({toStage.name})</span> : <span className="text-xs text-gray-400">(user picks stage)</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={"text-xs px-2 py-1 rounded-md font-medium " + styleObj.className}>{t.label}</span>
                      <Pencil className="h-3.5 w-3.5 text-gray-400" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {showWfTransForm && (
            <div className="mt-4 p-4 border border-brand-200 rounded-xl bg-brand-50/30">
              <WfTransitionForm
                workflows={workflows}
                screens={screens}
                onSave={function(data) {
                  adminApi.createWorkflowTransition(recordTypeId, data).then(function(res) {
                    setWfTransitions(function(prev) { return prev.concat([res.data.workflow_transition]); });
                    setShowWfTransForm(false);
                    toast.success('Cross-workflow transition added');
                  }).catch(function(err) { toast.error(err.response?.data?.error || 'Failed'); });
                }}
                onClose={function() { setShowWfTransForm(false); }}
              />
            </div>
          )}

          {editingWfTrans && (
            <div className="mt-4 p-4 border border-brand-200 rounded-xl bg-brand-50/30">
              <WfTransitionForm
                key={editingWfTrans.id}
                workflows={workflows}
                screens={screens}
                initial={editingWfTrans}
                onSave={function(data) {
                  adminApi.updateWorkflowTransition2(recordTypeId, editingWfTrans.id, data).then(function(res) {
                    setWfTransitions(function(prev) { return prev.map(function(x) { return x.id === editingWfTrans.id ? { ...x, ...res.data.workflow_transition } : x; }); });
                    setEditingWfTrans(null);
                    toast.success('Transition updated');
                  }).catch(function(err) { toast.error(err.response?.data?.error || 'Failed to update'); });
                }}
                onDelete={function() {
                  adminApi.deleteWorkflowTransition2(recordTypeId, editingWfTrans.id).then(function() {
                    setWfTransitions(function(prev) { return prev.filter(function(x) { return x.id !== editingWfTrans.id; }); });
                    setEditingWfTrans(null);
                    toast.success('Transition deleted');
                  }).catch(function() { toast.error('Failed to delete'); });
                }}
                onClose={function() { setEditingWfTrans(null); }}
              />
            </div>
          )}
        </div>
      )}

      {/* Save to Store Modal */}
      {showSaveToStore && workflow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={function() { setShowSaveToStore(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={function(e) { e.stopPropagation(); }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">Save to Template Store</h2>
              <button onClick={function() { setShowSaveToStore(false); }} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-500">Save this workflow as a reusable template. Other record types can install it from the Template Store.</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                <input type="text" value={saveForm.name} onChange={function(e) { setSaveForm(function(p) { return Object.assign({}, p, { name: e.target.value }); }); }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={saveForm.description} onChange={function(e) { setSaveForm(function(p) { return Object.assign({}, p, { description: e.target.value }); }); }}
                  rows={3} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Practice Area</label>
                <select value={saveForm.practice_area} onChange={function(e) { setSaveForm(function(p) { return Object.assign({}, p, { practice_area: e.target.value }); }); }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="general">General</option>
                  <option value="personal_injury">Personal Injury</option>
                  <option value="family_law">Family Law</option>
                  <option value="immigration">Immigration</option>
                  <option value="real_estate">Real Estate</option>
                  <option value="criminal">Criminal</option>
                  <option value="corporate">Corporate</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                  <select value={saveForm.icon} onChange={function(e) { setSaveForm(function(p) { return Object.assign({}, p, { icon: e.target.value }); }); }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
                    <option value="briefcase">Briefcase</option>
                    <option value="car">Car</option>
                    <option value="gavel">Gavel</option>
                    <option value="heart">Heart</option>
                    <option value="users">Users</option>
                    <option value="globe">Globe</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input type="color" value={saveForm.color} onChange={function(e) { setSaveForm(function(p) { return Object.assign({}, p, { color: e.target.value }); }); }}
                    className="w-full h-[38px] px-1 py-1 border border-gray-300 rounded-lg cursor-pointer" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={function() { setShowSaveToStore(false); }} className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
                <button onClick={async function() {
                  if (!saveForm.name.trim()) { toast.error("Name is required"); return; }
                  setSaveLoading(true);
                  try {
                    var res = await workflowTemplatesApi.saveFromWorkflow({
                      workflow_id: workflow.id,
                      name: saveForm.name,
                      description: saveForm.description,
                      practice_area: saveForm.practice_area,
                      icon: saveForm.icon,
                      color: saveForm.color
                    });
                    toast.success("Saved to Template Store! " + res.stats.stages + " stages, " + res.stats.tasks + " tasks, " + res.stats.screens + " screens, " + res.stats.fields + " fields");
                    setShowSaveToStore(false);
                  } catch (err) {
                    toast.error(err.response?.data?.error || "Failed to save");
                  } finally {
                    setSaveLoading(false);
                  }
                }} disabled={saveLoading}
                  className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 disabled:opacity-50">
                  {saveLoading ? "Saving..." : "Save to Store"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTemplateStore && <WorkflowTemplateStore recordTypeId={recordTypeId} onClose={() => setShowTemplateStore(false)} onInstalled={() => { setShowTemplateStore(false); fetchData(); }} />}
    </div>
  );
}

// Stage Editor
function StageEditor({ stage, screens, fields, workflowId, onUpdate, onSetInitial, onDelete, onClose }) {
  const [name, setName] = useState(stage.name);
  const [color, setColor] = useState(stage.color || '#6366F1');
  const [isFinal, setIsFinal] = useState(stage.settings?.is_final || false);
  const [requiredFields, setRequiredFields] = useState(stage.required_fields || []);
  const [stageScreens, setStageScreens] = useState([]);
  const [addScreenId, setAddScreenId] = useState('');
  const [addTabLabel, setAddTabLabel] = useState('');
  const [loadingSS, setLoadingSS] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [newDoc, setNewDoc] = useState({ document_category: '', label: '', is_mandatory: true });
  const DOC_CATEGORIES = [
    'medical_record','medical_bill','insurance_policy','insurance_correspondence',
    'legal_pleading','legal_correspondence','police_report','accident_report',
    'demand_letter','settlement_agreement','lien','photo_evidence',
    'employment_record','wage_loss','other'
  ];
  const fetchRequiredDocs = async () => {
    setLoadingDocs(true);
    try {
      const res = await adminApi.request('get', `/chat-ai/stage-required-docs/${stage.id}`);
      setRequiredDocs(res.data.docs || []);
    } catch(e) { console.error(e); }
    finally { setLoadingDocs(false); }
  };
  const handleAddDoc = async () => {
    if (!newDoc.document_category || !newDoc.label.trim()) return toast.error('Category and label required');
    try {
      await adminApi.request('post', `/chat-ai/stage-required-docs/${stage.id}`, { ...newDoc, sort_order: requiredDocs.length });
      setNewDoc({ document_category: '', label: '', is_mandatory: true });
      fetchRequiredDocs();
      toast.success('Required document added');
    } catch(e) { toast.error('Failed to add'); }
  };
  const handleDeleteDoc = async (docId) => {
    try {
      await adminApi.request('delete', `/chat-ai/stage-required-docs/${stage.id}/${docId}`);
      fetchRequiredDocs();
    } catch(e) { toast.error('Failed to delete'); }
  };
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [loadingTT, setLoadingTT] = useState(false);
  const [editingTT, setEditingTT] = useState(null);
  const [newTT, setNewTT] = useState({ title: '', description: '', priority: 'normal', category: 'general', due_days: 0, default_assignee_role: '', depends_on: [] });
  const [showAddTT, setShowAddTT] = useState(false);

  const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
  const CATEGORIES = ['general', 'admin', 'medical', 'legal', 'investigation', 'client_comm', 'discovery', 'settlement'];
  const ROLES = ['', 'attorney', 'paralegal', 'legal_assistant', 'case_manager'];

  useEffect(function() { if (activeTab === 'docs') fetchRequiredDocs(); }, [activeTab]);
  const fetchTaskTemplates = async () => {
    setLoadingTT(true);
    try {
      const res = await adminApi.getTaskTemplates(workflowId, stage.id);
      setTaskTemplates(res.data.templates);
    } catch (e) { console.error(e); }
    finally { setLoadingTT(false); }
  };

  const handleCreateTT = async () => {
    if (!newTT.title.trim()) return toast.error('Title required');
    try {
      await adminApi.createTaskTemplate(workflowId, stage.id, { ...newTT, sort_order: taskTemplates.length });
      setNewTT({ title: '', description: '', priority: 'normal', category: 'general', due_days: 0, default_assignee_role: '', depends_on: [] });
      setShowAddTT(false);
      fetchTaskTemplates();
      toast.success('Task template created');
    } catch (e) { toast.error('Failed to create template'); }
  };

  const handleUpdateTT = async (id, data) => {
    try {
      await adminApi.updateTaskTemplate(workflowId, stage.id, id, data);
      setEditingTT(null);
      fetchTaskTemplates();
    } catch (e) { toast.error('Failed to update template'); }
  };

  const handleDeleteTT = async (id) => {
    if (!window.confirm('Delete this task template?')) return;
    try {
      await adminApi.deleteTaskTemplate(workflowId, stage.id, id);
      fetchTaskTemplates();
      toast.success('Template deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  const fetchStageScreens = async () => {
    try {
      const res = await adminApi.getStageScreens(workflowId, stage.id);
      setStageScreens(res.data.stage_screens);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    setName(stage.name);
    setColor(stage.color || '#6366F1');
    setIsFinal(stage.settings?.is_final || false);
    setRequiredFields(stage.required_fields || []);
    fetchStageScreens();
    fetchTaskTemplates();
  }, [stage]);

  const handleSave = async () => {
    // Auto-add pending screen if one is selected but not yet added
    if (addScreenId) {
      try {
        await adminApi.addStageScreen(workflowId, stage.id, {
          screen_id: addScreenId,
          tab_label: addTabLabel || screens.find(function(s) { return s.id === addScreenId; })?.name || 'Tab',
        });
        setAddScreenId('');
        setAddTabLabel('');
        await fetchStageScreens();
      } catch (e) { toast.error('Failed to add screen'); }
    }
    onUpdate({
      name, color,
      screen_id: stageScreens.length > 0 ? stageScreens[0].screen_id : (addScreenId || null),
      settings: { ...stage.settings, is_final: isFinal },
      required_fields: requiredFields,
    });
  };

  const handleAddScreen = async () => {
    if (!addScreenId) return;
    setLoadingSS(true);
    try {
      await adminApi.addStageScreen(workflowId, stage.id, {
        screen_id: addScreenId,
        tab_label: addTabLabel || screens.find(function(s) { return s.id === addScreenId; })?.name || 'Tab',
      });
      setAddScreenId('');
      setAddTabLabel('');
      await fetchStageScreens();
    } catch (e) { toast.error('Failed to add screen'); }
    finally { setLoadingSS(false); }
  };

  const handleRemoveScreen = async (ssId) => {
    try {
      await adminApi.deleteStageScreen(workflowId, stage.id, ssId);
      await fetchStageScreens();
    } catch (e) { toast.error('Failed to remove screen'); }
  };

  const handleUpdateTabLabel = async (ssId, label) => {
    try {
      await adminApi.updateStageScreen(workflowId, stage.id, ssId, { tab_label: label });
      await fetchStageScreens();
    } catch (e) { toast.error('Failed to update tab label'); }
  };

  const usedScreenIds = stageScreens.map(function(ss) { return ss.screen_id; });
  const availableScreens = screens.filter(function(s) { return !usedScreenIds.includes(s.id); });

  const renderTaskTemplateForm = (tt, isNew) => (
    <div className="bg-gray-50 rounded-lg p-3 space-y-3 border border-gray-200">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
          <input type="text" value={isNew ? newTT.title : tt.title}
            onChange={function(e) { if (isNew) setNewTT({...newTT, title: e.target.value}); else setEditingTT({...tt, title: e.target.value}); }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="e.g., Send Letter of Representation" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
          <input type="text" value={isNew ? newTT.description : tt.description || ''}
            onChange={function(e) { if (isNew) setNewTT({...newTT, description: e.target.value}); else setEditingTT({...tt, description: e.target.value}); }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Brief description of the task" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
          <select value={isNew ? newTT.priority : tt.priority}
            onChange={function(e) { if (isNew) setNewTT({...newTT, priority: e.target.value}); else setEditingTT({...tt, priority: e.target.value}); }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500">
            {PRIORITIES.map(function(p) { return <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>; })}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
          <select value={isNew ? newTT.category : tt.category}
            onChange={function(e) { if (isNew) setNewTT({...newTT, category: e.target.value}); else setEditingTT({...tt, category: e.target.value}); }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500">
            {CATEGORIES.map(function(c) { return <option key={c} value={c}>{c.replace('_', ' ').replace(/\b\w/g, function(l) { return l.toUpperCase(); })}</option>; })}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Due (days after entry)</label>
          <input type="number" min="0" value={isNew ? newTT.due_days : tt.due_days}
            onChange={function(e) { var v = parseInt(e.target.value) || 0; if (isNew) setNewTT({...newTT, due_days: v}); else setEditingTT({...tt, due_days: v}); }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Default Assignee Role</label>
          <select value={isNew ? newTT.default_assignee_role : tt.default_assignee_role || ''}
            onChange={function(e) { if (isNew) setNewTT({...newTT, default_assignee_role: e.target.value}); else setEditingTT({...tt, default_assignee_role: e.target.value}); }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500">
            {ROLES.map(function(r) { return <option key={r} value={r}>{r ? r.replace('_', ' ').replace(/\b\w/g, function(l) { return l.toUpperCase(); }) : 'Unassigned'}</option>; })}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Depends On (must complete first)</label>
          <select multiple value={isNew ? newTT.depends_on : (tt.depends_on || [])}
            onChange={function(e) { var v = Array.from(e.target.selectedOptions, function(o) { return o.value; }); if (isNew) setNewTT({...newTT, depends_on: v}); else setEditingTT({...tt, depends_on: v}); }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 h-20">
            {taskTemplates.filter(function(t) { return isNew ? true : t.id !== tt.id; }).map(function(t) {
              return <option key={t.id} value={t.id}>{t.title}</option>;
            })}
          </select>
          <p className="text-[10px] text-gray-400 mt-0.5">Hold Ctrl/Cmd to select multiple</p>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={function() { if (isNew) setShowAddTT(false); else setEditingTT(null); }}
          className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
        <button onClick={function() { if (isNew) handleCreateTT(); else handleUpdateTT(tt.id, tt); }}
          className="px-3 py-1.5 text-xs font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700">
          {isNew ? 'Add Template' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-900">Edit Stage: {stage.name}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
      </div>
      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[{id:'general',label:'General'},{id:'screens',label:'Screens'},{id:'tasks',label:'Auto Tasks'},{id:'docs',label:'Required Docs'}].map(function(tab) {
          return (
            <button key={tab.id} onClick={function() { setActiveTab(tab.id); }}
              className={"px-3 py-2 text-xs font-medium border-b-2 transition-colors " + (activeTab === tab.id ? "border-brand-600 text-brand-700" : "border-transparent text-gray-500 hover:text-gray-700")}>
              {tab.label} {tab.id === 'tasks' ? '(' + taskTemplates.length + ')' : ''}
            </button>
          );
        })}
      </div>
      {activeTab === 'general' && (<><div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
          <input type="text" value={name} onChange={function(e) { setName(e.target.value); }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
          <div className="flex items-center gap-1.5">
            {STAGE_COLORS.map(function(cl) { return (
              <button key={cl} onClick={function() { setColor(cl); }}
                className={"h-7 w-7 rounded-full border-2 transition-all " + (color === cl ? "border-gray-900 scale-110" : "border-transparent")}
                style={{ backgroundColor: cl }} />
            ); })}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-xs font-medium text-gray-600 mb-1">Required Fields (to enter this stage)</label>
        <select multiple value={requiredFields} onChange={function(e) { setRequiredFields(Array.from(e.target.selectedOptions, function(o) { return o.value; })); }}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 h-24">
          {fields.map(function(f) { return <option key={f.id} value={f.id}>{f.label}</option>; })}
        </select>
        <p className="text-xs text-gray-400 mt-1">Hold Ctrl/Cmd to select multiple</p>
      </div>
      <div className="flex items-center gap-4 mt-4">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={stage.settings?.is_initial || false}
            onChange={function() { onSetInitial(); }} className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
          Initial stage (new records start here)
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={isFinal}
            onChange={function(e) { setIsFinal(e.target.checked); }} className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
          Final stage (marks record as complete)
        </label>
      </div>
      </>)}
      {activeTab === 'screens' && (
      <div>
      {/* Stage Screens (Tabs) */}
      <div className="mt-0">
        <label className="block text-xs font-medium text-gray-600 mb-2">Stage Screens (Tabs)</label>
        {stageScreens.length > 0 ? (
          <div className="space-y-2 mb-3">
            {stageScreens.map(function(ss, idx) { return (
              <div key={ss.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-xs text-gray-400 w-5">{idx + 1}.</span>
                <input type="text" value={ss.tab_label || ''} onBlur={function(e) { handleUpdateTabLabel(ss.id, e.target.value); }}
                  onChange={function(e) { setStageScreens(function(prev) { return prev.map(function(s) { return s.id === ss.id ? Object.assign({}, s, { tab_label: e.target.value }) : s; }); }); }}
                  className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="Tab label" />
                <span className="text-xs text-gray-400">{ss.screen_name}</span>
                <button onClick={function() { handleRemoveScreen(ss.id); }} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
              </div>
            ); })}
          </div>
        ) : (
          <p className="text-xs text-gray-400 mb-3">No screens assigned. Will use default screen.</p>
        )}
        {availableScreens.length > 0 && (
          <div className="flex items-center gap-2">
            <select value={addScreenId} onChange={function(e) { setAddScreenId(e.target.value); }}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500">
              <option value="">Add screen...</option>
              {availableScreens.map(function(s) { return <option key={s.id} value={s.id}>{s.name}</option>; })}
            </select>
            <input type="text" value={addTabLabel} onChange={function(e) { setAddTabLabel(e.target.value); }}
              placeholder="Tab label" className="w-28 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500" />
            <button onClick={handleAddScreen} disabled={!addScreenId || loadingSS}
              className="px-3 py-1.5 text-xs font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
              Add
            </button>
          </div>
        )}
      </div>
      </div>)}
      {activeTab === 'tasks' && (
        <TaskFlowEditor workflowId={workflowId} stageId={stage.id} stageName={stage.name} />
      )}
      {activeTab === 'docs' && (
        <div className="space-y-4">
          <p className="text-xs text-gray-500">Define documents required before or after this stage. The AI will check these and show a checklist to workers.</p>
          {loadingDocs ? <p className="text-xs text-gray-400">Loading...</p> : (
            <div className="space-y-2">
              {requiredDocs.map(function(doc) {
                return (
                  <div key={doc.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={doc.is_mandatory ? "w-2 h-2 rounded-full bg-red-500" : "w-2 h-2 rounded-full bg-yellow-400"} title={doc.is_mandatory ? 'Mandatory' : 'Recommended'} />
                      <span className="text-sm text-gray-700 font-medium">{doc.label}</span>
                      <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{doc.document_category.replace(/_/g,' ')}</span>
                    </div>
                    <button onClick={function() { handleDeleteDoc(doc.id); }} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
                  </div>
                );
              })}
              {requiredDocs.length === 0 && <p className="text-xs text-gray-400 italic">No required documents configured for this stage.</p>}
            </div>
          )}
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
            <p className="text-xs font-medium text-gray-600">Add Required Document</p>
            <select value={newDoc.document_category} onChange={function(e) { setNewDoc(function(p) { return {...p, document_category: e.target.value}; }); }}
              className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs">
              <option value="">Select category...</option>
              {DOC_CATEGORIES.map(function(c) { return <option key={c} value={c}>{c.replace(/_/g,' ')}</option>; })}
            </select>
            <input placeholder="Label (e.g. Retainer Agreement)" value={newDoc.label}
              onChange={function(e) { setNewDoc(function(p) { return {...p, label: e.target.value}; }); }}
              className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs" />
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={newDoc.is_mandatory}
                onChange={function(e) { setNewDoc(function(p) { return {...p, is_mandatory: e.target.checked}; }); }} />
              Mandatory (❌ if missing) vs Recommended (⚠️ if missing)
            </label>
            <button onClick={handleAddDoc} className="w-full py-1.5 text-xs font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700">
              + Add Document
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
        <button onClick={function() { onDelete(); }} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
          <Trash2 className="h-3.5 w-3.5" /> Delete Stage
        </button>
        <button onClick={handleSave}
          className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">
          Save Stage
        </button>
      </div>
    </div>
  );
}

// Transition Form
function TransitionForm({ stages, screens, initial, onSave, onDelete, onClose, onDirtyChange }) {
  const [fromStageId, setFromStageId] = useState(initial?.from_stage_id || '');
  const [toStageId, setToStageId] = useState(initial?.to_stage_id || '');
  const [label, setLabel] = useState(initial?.label || '');
  const [style, setStyle] = useState(initial?.style || 'primary');
  const [confirmMessage, setConfirmMessage] = useState(initial?.confirm_message || '');
  const [requireNote, setRequireNote] = useState(initial?.require_note || false);
  const [screenId, setScreenId] = useState(initial?.screen_id || '');

  // Report dirty state to parent when editing an existing transition
  useEffect(() => {
    if (!initial || !onDirtyChange) return;
    const dirty = fromStageId !== (initial.from_stage_id || '')
      || toStageId !== (initial.to_stage_id || '')
      || label !== (initial.label || '')
      || style !== (initial.style || 'primary')
      || confirmMessage !== (initial.confirm_message || '')
      || requireNote !== (initial.require_note || false)
      || screenId !== (initial.screen_id || '');
    onDirtyChange(dirty);
  }, [fromStageId, toStageId, label, style, confirmMessage, requireNote, screenId]);

  const handleSubmit = () => {
    if (!toStageId || !label.trim()) {
      toast.error('Label and target stage are required');
      return;
    }
    onSave({
      from_stage_id: fromStageId || null,
      to_stage_id: toStageId,
      label: label.trim(),
      style,
      confirm_message: confirmMessage || null,
      require_note: requireNote,
      screen_id: screenId || null,
    });
  };

  return (
    <div className="mt-4 p-4 border border-brand-200 rounded-xl bg-brand-50/30">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-700">{initial ? 'Edit Transition' : 'New Transition'}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From Stage</label>
          <select value={fromStageId} onChange={e => setFromStageId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
            <option value="">Any stage</option>
            {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To Stage *</label>
          <select value={toStageId} onChange={e => setToStageId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
            <option value="">Select...</option>
            {stages.filter(s => s.id !== fromStageId).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Button Label *</label>
          <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Move to Review"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Button Style</label>
          <select value={style} onChange={e => setStyle(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
            {TRANSITION_STYLES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Confirm Message (optional)</label>
          <input type="text" value={confirmMessage} onChange={e => setConfirmMessage(e.target.value)}
            placeholder="Are you sure?"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={requireNote} onChange={e => setRequireNote(e.target.checked)}
              className="rounded border-gray-300 text-brand-600" />
            Require note
          </label>
        </div>
      </div>
      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">Transition Form (optional)</label>
        <select value={screenId} onChange={function(e) { setScreenId(e.target.value); }}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
          <option value="">No form — transition immediately</option>
          {screens.map(function(s) { return <option key={s.id} value={s.id}>{s.name}</option>; })}
        </select>
        <p className="text-xs text-gray-400 mt-1">If set, a form pops up before the record moves.</p>
      </div>
      <div className="flex items-center justify-between mt-4">
        {initial && onDelete ? (
          <button onClick={onDelete} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
            <Trash2 className="h-.5 w-3.5" /> Delete
          </button>
        ) : <div />}
        <button onClick={handleSubmit}
          className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">
          {initial ? 'Update' : 'Add'} Transition
        </button>
      </div>
    </div>
  );
}

// Workflow-to-Workflow Transition Form
function WfTransitionForm({ workflows, screens, initial, onSave, onDelete, onClose }) {
  const [fromWfId, setFromWfId] = useState(initial?.from_workflow_id || '');
  const [fromStageId, setFromStageId] = useState(initial?.from_stage_id || '');
  const [toWfId, setToWfId] = useState(initial?.to_workflow_id || '');
  const [toStageId, setToStageId] = useState(initial?.to_stage_id || '');
  const [label, setLabel] = useState(initial?.label || '');
  const [style, setStyle] = useState(initial?.style || 'primary');
  const [screenId, setScreenId] = useState(initial?.screen_id || '');
  const [confirmMessage, setConfirmMessage] = useState(initial?.confirm_message || '');
  const [requireNote, setRequireNote] = useState(initial?.require_note || false);

  var fromWf = workflows.find(function(w) { return w.id === fromWfId; });
  var toWf = workflows.find(function(w) { return w.id === toWfId; });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-700">{initial ? 'Edit Workflow Transition' : 'New Workflow Transition'}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From Workflow</label>
          <select value={fromWfId} onChange={function(e) { setFromWfId(e.target.value); setFromStageId(''); }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
            <option value="">Any workflow</option>
            {workflows.map(function(w) { return <option key={w.id} value={w.id}>{w.name}</option>; })}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From Stage (optional)</label>
          <select value={fromStageId} onChange={function(e) { setFromStageId(e.target.value); }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" disabled={!fromWfId}>
            <option value="">Any stage</option>
            {fromWf && fromWf.stages && fromWf.stages.map(function(s) { return <option key={s.id} value={s.id}>{s.name}</option>; })}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To Workflow *</label>
          <select value={toWfId} onChange={function(e) { setToWfId(e.target.value); setToStageId(''); }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
            <option value="">Select...</option>
            {workflows.filter(function(w) { return w.id !== fromWfId; }).map(function(w) { return <option key={w.id} value={w.id}>{w.name}</option>; })}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To Stage (entry point)</label>
          <select value={toStageId} onChange={function(e) { setToStageId(e.target.value); }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" disabled={!toWfId}>
            <option value="">First stage (default)</option>
            {toWf && toWf.stages && toWf.stages.map(function(s) { return <option key={s.id} value={s.id}>{s.name}</option>; })}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Button Label *</label>
          <input type="text" value={label} onChange={function(e) { setLabel(e.target.value); }}
            placeholder="e.g. Move to Litigation" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Style</label>
          <select value={style} onChange={function(e) { setStyle(e.target.value); }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
            {TRANSITION_STYLES.map(function(s) { return <option key={s.value} value={s.value}>{s.label}</option>; })}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Transition Form</label>
          <select value={screenId} onChange={function(e) { setScreenId(e.target.value); }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
            <option value="">No form</option>
            {screens.map(function(s) { return <option key={s.id} value={s.id}>{s.name}</option>; })}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Confirm Message</label>
          <input type="text" value={confirmMessage} onChange={function(e) { setConfirmMessage(e.target.value); }}
            placeholder="Are you sure?" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={requireNote} onChange={function(e) { setRequireNote(e.target.checked); }}
              className="rounded border-gray-300 text-brand-600" />
            Require note
          </label>
        </div>
      </div>
      <div className="flex items-center justify-between mt-4">
        {initial && onDelete ? (
          <button onClick={onDelete} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        ) : <div />}
        <button onClick={function() {
            if (!toWfId || !label.trim()) { toast.error('Label and target workflow required'); return; }
            onSave({
              from_workflow_id: fromWfId || null,
              from_stage_id: fromStageId || null,
              to_workflow_id: toWfId,
              to_stage_id: toStageId || null,
              label: label.trim(),
              style: style,
              screen_id: screenId || null,
              confirm_message: confirmMessage || null,
              require_note: requireNote,
            });
          }}
          className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">
          {initial ? 'Update' : 'Add'} Workflow Transition
        </button>
      </div>
    </div>
  );
}

